import React, { useState, useEffect, useCallback } from 'react';
import { FiSearch, FiCheck, FiX, FiChevronLeft, FiChevronRight, FiRefreshCw } from 'react-icons/fi';
import ApproveModal from './ApproveModal';
import '../../styles/AdminDashboard.css';

const PAGE_SIZE = 10;

interface ClawRequest {
  userId:     number;
  workEmail:  string;
  firstName:  string;
  lastName:   string;
  created:    string;
  isApproved: boolean;
}

interface Props {
  onCountChange?: (count: number) => void;
}

/* ── Pagination ── */
const ClawPagination: React.FC<{ page: number; total: number; onPage: (p: number) => void }> = ({ page, total, onPage }) => {
  const totalPages = Math.ceil(total / PAGE_SIZE);
  if (totalPages <= 1) return null;
  const start = (page - 1) * PAGE_SIZE + 1;
  const end   = Math.min(page * PAGE_SIZE, total);

  const pageNums = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
    .reduce<(number | string)[]>((acc, p, i, arr) => {
      if (i > 0 && (p as number) - (arr[i - 1] as number) > 1) acc.push('…');
      acc.push(p);
      return acc;
    }, []);

  return (
    <div className="admin-pagination">
      <span className="admin-pagination__info">Showing {start}–{end} of {total}</span>
      <div className="admin-pagination__controls">
        <button className="admin-pagination__btn" onClick={() => onPage(page - 1)} disabled={page === 1}>
          <FiChevronLeft size={13} />
        </button>
        {pageNums.map((p, i) =>
          typeof p === 'string'
            ? <span key={`e${i}`} className="admin-pagination__ellipsis">{p}</span>
            : <button
                key={p}
                className={`admin-pagination__btn${page === p ? ' admin-pagination__btn--active' : ''}`}
                onClick={() => onPage(p as number)}
              >{p}</button>
        )}
        <button className="admin-pagination__btn" onClick={() => onPage(page + 1)} disabled={page === totalPages}>
          <FiChevronRight size={13} />
        </button>
      </div>
    </div>
  );
};

/* ── Main component ── */
const ClawRequestsTab: React.FC<Props> = ({ onCountChange }) => {
  const [requests,   setRequests]   = useState<ClawRequest[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [search,     setSearch]     = useState('');
  const [page,       setPage]       = useState(1);
  const [approved,   setApproved]   = useState<Set<number>>(new Set());
  const [rejected,   setRejected]   = useState<Set<number>>(new Set());
  const [actioning,  setActioning]  = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [approving,  setApproving]  = useState<ClawRequest | null>(null);

  const fetchRequests = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('authToken') || '';
      const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/clawrequest/requests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      const data: ClawRequest[] = await res.json();
      setRequests(data);
      onCountChange?.(data.length);
      setPage(1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load claw requests');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [onCountChange]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleApprove = (req: ClawRequest) => {
    setApproving(req);
  };

  const handleApproved = (userId: number) => {
    setApproved(prev => new Set(prev).add(userId));
    setRejected(prev => { const s = new Set(prev); s.delete(userId); return s; });
    setApproving(null);
  };

  const handleReject = (userId: number) => {
    setActioning(userId);
    setTimeout(() => {
      setRejected(prev => new Set(prev).add(userId));
      setApproved(prev => { const s = new Set(prev); s.delete(userId); return s; });
      setActioning(null);
    }, 600);
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  const fullName = (r: ClawRequest) => {
    const name = [r.firstName, r.lastName].filter(Boolean).join(' ');
    return name || '—';
  };

  const filtered = search.trim()
    ? requests.filter(r =>
        r.workEmail.toLowerCase().includes(search.toLowerCase()) ||
        fullName(r).toLowerCase().includes(search.toLowerCase()) ||
        String(r.userId).includes(search)
      )
    : requests;

  const slice = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <>
      {approving && (
        <ApproveModal
          userId={approving.userId}
          userEmail={approving.workEmail}
          userName={`${approving.firstName} ${approving.lastName}`.trim()}
          onClose={() => setApproving(null)}
          onApproved={() => handleApproved(approving.userId)}
        />
      )}

      {/* Search + Refresh */}
      <div className="admin-search-bar">
        <div className="admin-search-bar__input-wrap">
          <FiSearch size={15} className="admin-search-bar__icon" />
          <input
            type="text"
            className="admin-search-bar__input"
            placeholder="Search by email, name, or user ID..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        <button
          className="admin-refresh-btn admin-refresh-btn--claw"
          onClick={() => fetchRequests(true)}
          disabled={refreshing}
        >
          <FiRefreshCw size={13} className={refreshing ? 'admin-spin' : ''} />
          <span>Refresh</span>
        </button>
      </div>

      <div className="admin-table-wrapper">
        {error && <div className="admin-error">{error}</div>}

        {loading ? (
          <div className="admin-loading">
            {[...Array(5)].map((_, i) => <div key={i} className="admin-skeleton-row" />)}
          </div>
        ) : (
          <>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>User ID</th>
                  <th>Email</th>
                  <th>Full Name</th>
                  <th>Requested On</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {slice.length === 0 ? (
                  <tr><td colSpan={6} className="admin-table__empty">No Momentum Claw requests found</td></tr>
                ) : slice.map((req, idx) => {
                  const approvedLocally = approved.has(req.userId);
                  const isRejected     = rejected.has(req.userId);
                  const isActioning    = actioning === req.userId;
                  // Already approved in DB, or approved this session
                  const isDone         = req.isApproved || approvedLocally;

                  return (
                    <tr key={req.userId}>
                      <td className="admin-table__index">{(page - 1) * PAGE_SIZE + idx + 1}</td>
                      <td>
                        <span className="admin-claw-table__user-id">
                          #{req.userId}
                        </span>
                      </td>
                      <td className="admin-table__name">{req.workEmail}</td>
                      <td className="admin-claw-table__full-name">{fullName(req)}</td>
                      <td className="admin-claw-table__requested-on">
                        {formatDate(req.created)}
                      </td>
                      <td>
                        {isDone ? (
                          <span className="admin-claw-action-badge admin-claw-action-badge--done">
                            <FiCheck size={11} /> Done
                          </span>
                        ) : isRejected ? (
                          <span className="admin-claw-action-badge admin-claw-action-badge--rejected">
                            <FiX size={11} /> Rejected
                          </span>
                        ) : (
                          <div className="admin-claw-actions">
                            <button
                              className="admin-claw-btn admin-claw-btn--approve"
                              onClick={() => handleApprove(req)}
                              disabled={isActioning}
                              title="Approve this request"
                            >
                              {isActioning
                                ? <span className="admin-claw-btn__spinner" />
                                : <FiCheck size={12} />
                              }
                              Approve
                            </button>
                            <button
                              className="admin-claw-btn admin-claw-btn--reject"
                              onClick={() => handleReject(req.userId)}
                              disabled={isActioning}
                              title="Reject this request"
                            >
                              <FiX size={12} />
                              Reject
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <ClawPagination page={page} total={filtered.length} onPage={setPage} />
          </>
        )}
      </div>
    </>
  );
};

export default ClawRequestsTab;

